import { RefObject, useCallback } from "react";
import {
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
  TextInputProps,
  TextInputSelectionChangeEventData,
} from "react-native";

import MobileNumberInputView from "./MobileNumberInputView";
import formatPhoneNumber from "@/utils/formatPhoneNumber";

/**
 * Props for MobileNumberInputContainer component
 */
interface MobileNumberInputContainerProps {
  readonly inputRef: RefObject<TextInputProps>;
  readonly value: string;
  readonly countryCode: string;
  readonly onChange: (value: string) => void;
  readonly onBlur: () => void;
  readonly onSubmit: () => void;
  readonly onSelectionChange: (
    e: NativeSyntheticEvent<TextInputSelectionChangeEventData>
  ) => void;
  readonly onKeyPress: (
    e: NativeSyntheticEvent<TextInputKeyPressEventData>
  ) => void;
  readonly maxLength: number;
  readonly isDark: boolean;
}

/**
 * Mobile number input container component handling formatting logic
 *
 * Manages phone number formatting using the selected country code.
 * Passes formatted input callbacks to the View component.
 * @param root0 - Component props
 * @param root0.inputRef - Ref to input field for cursor control
 * @param root0.value - Current input value
 * @param root0.countryCode - Country code used for phone number formatting
 * @param root0.onChange - Callback invoked with formatted phone number
 * @param root0.onBlur - Callback when input loses focus
 * @param root0.onSubmit - Callback when done key is pressed
 * @param root0.onSelectionChange - Callback for selection changes
 * @param root0.onKeyPress - Callback for key press events
 * @param root0.maxLength - Maximum input length based on country code
 * @param root0.isDark - Whether dark theme is active
 */
const MobileNumberInputContainer = ({
  inputRef,
  value,
  countryCode,
  onChange,
  onBlur,
  onSubmit,
  onSelectionChange,
  onKeyPress,
  maxLength,
  isDark,
}: MobileNumberInputContainerProps) => {
  const handleChangeText = useCallback(
    (inputValue: string) => {
      onChange(formatPhoneNumber(inputValue, false, countryCode));
    },
    [countryCode, onChange]
  );

  return (
    <MobileNumberInputView
      inputRef={inputRef}
      value={value}
      onChangeText={handleChangeText}
      onBlur={onBlur}
      onSubmitEditing={onSubmit}
      onSelectionChange={onSelectionChange}
      onKeyPress={onKeyPress}
      maxLength={maxLength}
      isDark={isDark}
    />
  );
};

export default MobileNumberInputContainer;
